import { useEffect } from "react";
import { formatAmount, usePrevious } from "../../../core";
import { FinancialField, FinancialFieldProps } from "../../inputs";
import { TableColumnDef } from "../types";
import { defaultPrettifyFilterValue } from "../utils";

import styles from "./styles.module.scss";

export function FinancialColumn<T extends object = {}>(
  { financialFieldProps, secondFinancialFieldProps, ...params }: TableColumnDef<T> & {
    financialFieldProps?: FinancialFieldProps,
    secondFinancialFieldProps?: FinancialFieldProps
  },
): TableColumnDef<T> {
  return {
    enableColumnFilter: true,
    minSize: 50,
    cell: props => {
      const prettified = formatAmount(props.getValue() as any);
      return (
        <span className={styles.content} style={{ width: "100%", textAlign: "right", display: "block" }}>
          {prettified}
        </span>
      );
    },
    ...params,
    meta: {
      defaultFilterFn: "inNumberRange",
      additionalFilterFn: [ "equals", "inNumberRange" ],
      prettifyFilterValue: ((v: any, filterFn) => {
        if (filterFn === "inNumberRange") {
          if (Array.isArray(v)) {
            const low = v[0];
            const high = v[1];

            if (
              low !== null && low !== undefined &&
              high !== null && high !== undefined
            ) {
              return ({ value: `${low} - ${high}` });
            } else if (low !== null && low !== undefined) {
              return ({ value: `More than ${low}` });
            } else if (high !== null && high !== undefined) {
              return ({ value: `Up to ${high}` });
            }
          }
        }

        return defaultPrettifyFilterValue(v, filterFn);
      }),
      Filter: ({ column, label, value, onChange, autoFocus, filterFn }) => {
        const previousFilterFn = usePrevious(filterFn);

        useEffect(() => {
          if (!!previousFilterFn && previousFilterFn !== filterFn) {
            if (filterFn === "inNumberRange") {
              onChange([ value, null ]);
            } else {
              onChange(Array.isArray(value) ? value?.[0] : null);
            }
          }
        }, [ filterFn, onChange, previousFilterFn, value ]);


        if (filterFn === "inNumberRange") {
          return (
            <>
              <FinancialField
                autoFocus={autoFocus}
                placeholder="From"
                value={Array.isArray(value) ? value?.[0] : null}
                onChange={v => onChange([ v, Array.isArray(value) ? value?.[1] : null ])}
                {...financialFieldProps}
              />
              <FinancialField
                placeholder="To"
                value={Array.isArray(value) ? value?.[1] : null}
                onChange={v => onChange([ Array.isArray(value) ? value?.[0] : null, v ])}
                {...secondFinancialFieldProps}
              />
            </>
          );
        }
        return (
          <FinancialField
            autoFocus={autoFocus}
            label={label}
            value={value as any}
            onChange={onChange}
            {...financialFieldProps}
          />
        );
      },
      ...params?.meta,
      excelProps: {
        numFmt: "$#,##0.00;[Red]-$#,##0.00",
        totalsRowFunction: "sum",
        filterButton: true,
        ...params?.meta?.excelProps,
      },
    },
  };
}
